import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, Sparkles, Calendar } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const scrollToId = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

/* ---------- Magnetic button: cursor-following CTA ---------- */
const MagneticButton: React.FC<React.PropsWithChildren<{
  onClick?: () => void;
  className?: string;
}>> = ({ children, onClick, className }) => {
  const ref = useRef<HTMLButtonElement>(null);

  const onMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - (rect.left + rect.width / 2);
    const my = e.clientY - (rect.top + rect.height / 2);
    // Soft magnetic pull, capped to keep it natural
    el.style.setProperty('--mx', `${mx * 0.18}px`);
    el.style.setProperty('--my', `${my * 0.25}px`);
  };

  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--mx', '0px');
    el.style.setProperty('--my', '0px');
  };

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`magnetic-btn ${className ?? ''}`}
    >
      {children}
    </button>
  );
};

/* ---------- Animated number / text counter ---------- */
const AnimatedStat: React.FC<{ value: string; inView: boolean; delay?: number }> = ({
  value,
  inView,
  delay = 0,
}) => {
  // Try to parse a leading numeric portion; otherwise just fade in the literal.
  const numMatch = value.match(/^(\+?)(\d+)(.*)$/);
  const ref = useRef<HTMLSpanElement>(null);
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1600, bounce: 0 });
  const display = useTransform(spring, (latest) =>
    numMatch ? `${numMatch[1]}${Math.round(latest)}${numMatch[3]}` : value
  );

  useEffect(() => {
    if (!inView) return;
    const target = numMatch ? parseInt(numMatch[2], 10) : 0;
    const t = window.setTimeout(() => mv.set(target), delay);
    return () => window.clearTimeout(t);
  }, [inView, delay, mv, numMatch]);

  useEffect(() => {
    return display.on('change', (v) => {
      if (ref.current) ref.current.textContent = String(v);
    });
  }, [display]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0, y: 8 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
      transition={{ duration: 0.6, delay: delay / 1000 }}
    >
      {numMatch ? `${numMatch[1]}0${numMatch[3]}` : value}
    </motion.span>
  );
};

/* ---------- Letter-stagger headline ---------- */
const StaggerWords: React.FC<{ text: string; className?: string; delay?: number }> = ({
  text,
  className,
  delay = 0,
}) => {
  const words = text.split(' ');
  return (
    <span className={className} aria-label={text}>
      {words.map((word, i) => (
        <motion.span
          key={`${word}-${i}`}
          className="inline-block"
          initial={{ opacity: 0, y: 28, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{
            duration: 0.7,
            delay: delay + i * 0.05,
            ease: [0.22, 1, 0.36, 1],
          }}
          aria-hidden="true"
        >
          {word}
          {i < words.length - 1 && ' '}
        </motion.span>
      ))}
    </span>
  );
};

/* ---------- Main hero ---------- */
const HeroSection: React.FC = () => {
  const { t } = useLanguage();
  const [pointer, setPointer] = useState({ x: 50, y: 50 });
  const sectionRef = useRef<HTMLElement | null>(null);
  const statsRef = useRef<HTMLDivElement | null>(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.3 });

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      setPointer({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const marqueeItems = (t('marquee.items') as unknown as string[]) ?? [];
  const items = Array.isArray(marqueeItems) ? marqueeItems : [];

  return (
    <section
      ref={sectionRef}
      className="relative isolate overflow-x-hidden bg-background pt-10 md:pt-20"
      aria-label="Hero"
    >
      {/* Aurora drift backdrop */}
      <div className="absolute inset-0 -z-10 aurora-bg" aria-hidden="true" />

      {/* Cursor spotlight */}
      <div
        className="absolute inset-0 -z-10 opacity-80 transition-opacity duration-500"
        aria-hidden="true"
        style={{
          background: `radial-gradient(560px circle at ${pointer.x}% ${pointer.y}%, hsl(var(--primary) / 0.18), transparent 60%)`,
        }}
      />

      {/* Floating orbs — smaller on mobile to avoid overflow */}
      <div className="orb orb-violet top-[12%] left-[8%] h-40 w-40 md:h-72 md:w-72" aria-hidden="true" />
      <div className="orb orb-cyan   top-[40%] right-[6%] h-36 w-36 md:h-64 md:w-64" aria-hidden="true" />
      <div className="orb orb-pink   bottom-[18%] left-[28%] h-32 w-32 md:h-56 md:w-56" aria-hidden="true" />

      {/* Dot grid + top accent bar */}
      <div className="absolute inset-0 -z-10 dot-grid opacity-[0.3]" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 -z-10 accent-bar opacity-80" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Editorial top bar */}
        <motion.div
          className="flex items-center justify-between gap-4 pb-8 md:pb-14"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="pill-tag-lime">
            <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
            {t('hero.eyebrow')}
          </span>
          <span className="hidden md:inline-flex font-mono-tag text-[11px] uppercase tracking-[0.32em] text-muted-foreground">
            v.2026 / studio
          </span>
        </motion.div>

        {/* Main editorial grid */}
        <div className="grid grid-cols-12 gap-y-8 md:gap-y-12">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-9">
            <h1 className="font-display text-foreground">
              <span className="block text-[clamp(2.5rem,6.5vw,5.75rem)] leading-[0.95] tracking-tight">
                <StaggerWords text={t('hero.titleStart')} delay={0.1} />
              </span>
              <span className="block text-[clamp(2.5rem,6.5vw,5.75rem)] leading-[0.95] tracking-tight italic text-muted-foreground/90">
                <motion.span
                  className="relative inline-block not-italic font-display gradient-text-shimmer mr-3"
                  initial={{ opacity: 0, scale: 0.94, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  transition={{ duration: 1.0, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {t('hero.titleHighlight')}
                  <svg
                    className="absolute -bottom-1 left-0 w-full"
                    height="14"
                    viewBox="0 0 300 14"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="hl-grad" x1="0" x2="1">
                        <stop offset="0%"   stopColor="hsl(var(--primary))" />
                        <stop offset="50%"  stopColor="hsl(var(--lime))" />
                        <stop offset="100%" stopColor="hsl(var(--magenta))" />
                      </linearGradient>
                    </defs>
                    <motion.path
                      d="M2 8 C 80 2, 200 12, 298 6"
                      stroke="url(#hl-grad)"
                      strokeWidth="2.5"
                      fill="none"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 1.2, delay: 1.0, ease: 'easeInOut' }}
                    />
                  </svg>
                </motion.span>
                <StaggerWords text={t('hero.titleEnd')} delay={0.7} />
              </span>
            </h1>
          </div>

          {/* Side note */}
          <motion.aside
            className="col-span-12 lg:col-span-3 lg:pl-6 lg:border-l lg:border-border/60"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <p className="eyebrow mb-3">— para</p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </motion.aside>

          {/* CTA cluster */}
          <motion.div
            className="col-span-12 flex flex-col xs:flex-row items-stretch gap-3 pt-2"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            {/* Magnetic primary CTA */}
            <MagneticButton onClick={() => scrollToId('contact')}>
              <span className="group relative inline-flex items-center justify-center gap-2 w-full h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light text-base font-medium shadow-glow border border-primary-light/30 transition-colors">
                {/* Pulse rings */}
                <span className="pointer-events-none absolute inset-0 rounded-full border border-primary-light/40 animate-pulse-ring" aria-hidden="true" />
                <Calendar className="h-4 w-4" />
                {t('hero.cta')}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </MagneticButton>

            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToId('services')}
              className="h-14 px-7 text-base font-medium border-border/80 bg-card/40 hover:bg-card hover:border-foreground/40 rounded-full w-full xs:w-auto"
            >
              {t('hero.ctaSecondary')}
            </Button>

            <div className="hidden md:flex items-center gap-2 pl-4 ml-auto font-mono-tag text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-lime" />
              Resposta &lt; 24h
            </div>
          </motion.div>

          {/* Stats strip with animated counters */}
          <motion.div
            ref={statsRef}
            className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            {[
              ['stat1Value', 'stat1Label'],
              ['stat2Value', 'stat2Label'],
              ['stat3Value', 'stat3Label'],
              ['stat4Value', 'stat4Label'],
            ].map(([v, l], i) => (
              <div
                key={v}
                className="group relative bg-card/70 backdrop-blur-sm p-5 md:p-6 transition-all hover:bg-card overflow-hidden"
              >
                {/* Hover glow */}
                <div
                  className="pointer-events-none absolute -bottom-12 -right-12 h-32 w-32 rounded-full bg-primary/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  aria-hidden="true"
                />
                <div className="relative">
                  <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-3">
                    / 0{i + 1}
                  </div>
                  <div className="font-display text-3xl md:text-4xl text-foreground leading-none mb-2 tabular-nums">
                    <AnimatedStat
                      value={t(`hero.${v}`)}
                      inView={statsInView}
                      delay={200 + i * 150}
                    />
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">
                    {t(`hero.${l}`)}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Marquee / trust ticker */}
        <motion.div
          className="relative mt-14 md:mt-20 -mx-6 lg:-mx-8 overflow-hidden marquee-pause"
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10" />
          <div className="flex marquee-track gap-12 py-4 border-y border-border/40">
            {[...items, ...items].map((item, idx) => (
              <span
                key={idx}
                className="whitespace-nowrap font-mono-tag text-sm uppercase tracking-[0.24em] text-muted-foreground/80"
              >
                <span className="mr-12 text-lime/80">✦</span>
                {item}
              </span>
            ))}
          </div>
        </motion.div>

        <div className="h-16 md:h-24" />
      </div>
    </section>
  );
};

export default HeroSection;
